import { useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactElement } from 'react';

import { ApiError } from '../lib/api';

/**
 * Composer at the foot of the centre column.
 *
 * Enter sends and Shift+Enter breaks the line, which is the convention every
 * chat user already has in their fingers.
 *
 * The typing notice is throttled here rather than in the store because this is
 * where keystrokes happen: a frame goes out at most once every three seconds
 * per author, and the indicator on the other end expires on its own, so the
 * cost of saying "still typing" is bounded no matter how fast anyone types.
 */

const TYPING_INTERVAL_MS = 3_000;

const GENERIC_FAILURE = 'Não foi possível enviar a mensagem. Tente de novo.';

export interface MessageComposerProps {
  /** Name of the open channel, used in the placeholder and the accessible label. */
  channelName: string;
  onSend: (content: string) => Promise<void> | void;
  onTyping: () => void;
  /**
   * Receives the file picked with the clip. Left out where there is nowhere to
   * send one, and the clip is then not rendered at all: a clip that attaches
   * nothing is worse than no clip.
   */
  onAttach?: (file: File) => void;
  disabled?: boolean;
}

export function MessageComposer({
  channelName,
  onSend,
  onTyping,
  onAttach,
  disabled = false,
}: MessageComposerProps): ReactElement {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastTypingAt = useRef(0);

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    setValue(event.target.value);

    if (event.target.value.trim() === '') {
      return;
    }

    const now = Date.now();
    if (now - lastTypingAt.current < TYPING_INTERVAL_MS) {
      return;
    }

    lastTypingAt.current = now;
    onTyping();
  }

  async function submit(): Promise<void> {
    const content = value.trim();
    if (content === '' || sending || disabled) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      await onSend(content);
      setValue('');
    } catch (cause) {
      // The text stays in the box: a refusal — a rate limit, above all — must
      // never cost someone what they wrote.
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    void submit();
  }

  /**
   * The field is emptied after the file is handed over. Without it, picking the
   * same file twice in a row does nothing the second time: the value did not
   * change, so the browser reports nothing.
   */
  function handleFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (file) {
      onAttach?.(file);
    }
  }

  return (
    <div className="composer">
      {error !== null && (
        <p className="composer-error" role="alert">
          {error}
        </p>
      )}

      <div className="composer-row">
        {onAttach !== undefined && (
          <label className="composer-clip" title="Anexar arquivo">
            <span className="composer-clip-sigil" aria-hidden="true">
              📎
            </span>
            {/* The input itself carries the accessible name and is hidden by
                the stylesheet: a file picker styled by hand is a picker no
                keyboard reaches. */}
            <input
              type="file"
              className="composer-file"
              aria-label="Anexar arquivo"
              disabled={disabled}
              onChange={handleFile}
            />
          </label>
        )}

        <textarea
          className="composer-field"
          aria-label={`Mensagem em #${channelName}`}
          placeholder={`Conversar em #${channelName}`}
          rows={1}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
}

export default MessageComposer;
